import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../utils/axiosConfig";
import { generateTCDSPdf } from "../utils/tcdsPdfGenerator";
import { generateAPI6DPdf } from "../utils/Generateapi6d";
import { generateTCDSExcel } from "../utils/generateTCDSExcel";
import "./ManageTCDS.css";
import "./ViewTCDS.css";

function ManageTCDS() {
  const navigate = useNavigate();
  const [tcdsList, setTcdsList] = useState([]);
  const [filteredTcdsList, setFilteredTcdsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTCDS, setSelectedTCDS] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [selectedTcdsType, setSelectedTcdsType] = useState("Automation/Standard");
  const [searchTerm, setSearchTerm] = useState("");

  // Unit selection state for admin
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState("");

  // Role check
  const userRole = (localStorage.getItem("role") || "").toLowerCase();
  const canCreate = userRole === "sales" || userRole === "admin";
  const isAdmin = userRole === "admin";

  // Available units for admin
  const units = [
    { id: 1, name: "Unit-1" },
    { id: 2, name: "Unit-2" },
  ];

  const normalizeTcdsType = (type) => {
    const normalized = String(type || "").trim().toLowerCase();
    if (normalized === "api 6d") return "API 6D";
    if (normalized === "api 600") return "API 600";
    return "Automation/Standard";
  };

  const getRouteForType = (type, id = null) => {
    const normalizedType = normalizeTcdsType(type);

    if (normalizedType === "API 6D") {
      return id ? `/tcds/api-6d/edit/${id}` : "/tcds/api-6d/new";
    }
    if (normalizedType === "API 600") {
      return id ? `/tcds/api-600/edit/${id}` : "/tcds/api-600/new";
    }
    return id ? `/tcds/edit/${id}` : "/tcds/new";
  };

  const getDeleteEndpointForType = (type, id) => {
    const normalizedType = normalizeTcdsType(type);
    if (normalizedType === "API 6D") return `/tcds/api-6d/${id}?hard=true`;
    if (normalizedType === "API 600") return `/tcds/api-600/${id}?hard=true`;
    return `/tcds/${id}?hard=true`;
  };

  const getByIdEndpointForType = (type, id) => {
    const normalizedType = normalizeTcdsType(type);
    if (normalizedType === "API 6D") return `/tcds/api-6d/${id}`;
    if (normalizedType === "API 600") return `/tcds/api-600/${id}`;
    return `/tcds/${id}`;
  };

  const normalizeText = (text) => {
    if (!text) return "";
    return String(text).toLowerCase().replace(/\s+/g, " ").trim();
  };

  const extractJsonText = (jsonStr) => {
    if (!jsonStr) return "";
    try {
      const parsed = typeof jsonStr === "string" ? JSON.parse(jsonStr) : jsonStr;
      return Object.values(parsed || {}).join(" ");
    } catch {
      return "";
    }
  };

  const recordLooksLikeGateValve = (record) => {
    const keywords = ["gate"];
    const candidates = [
      record.type,
      record.section_title,
      record.section_number,
      record.make,
      record.series,
      record.product_fg_code,
      record.tcds_type,
      record.customer_item_code,
      extractJsonText(record.api600_data),
      extractJsonText(record.dynamic_parameters),
      extractJsonText(record.accessories_data),
    ].map(normalizeText);
    return keywords.some((kw) => candidates.some((text) => text.includes(kw)));
  };

  const fetchTCDS = async (tcdsType = "Automation/Standard") => {
    try {
      setLoading(true);
      const normalizedType = normalizeTcdsType(tcdsType);
      const response = await axios.get("/tcds", {
        params: { tcds_type: normalizedType },
      });
      setTcdsList(response.data.data || response.data);
      setFilteredTcdsList(response.data.data || response.data);
      setSearchTerm("");
    } catch (error) {
      setError(error.response?.data?.message || "Failed to fetch TCDS records");
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = async (tcdsType) => {
    const normalizedType = normalizeTcdsType(tcdsType);
    setSelectedTcdsType(normalizedType);
    setShowTableModal(true);
    await fetchTCDS(normalizedType);
  };

  const closeTableModal = () => {
    setShowTableModal(false);
    setTcdsList([]);
    setFilteredTcdsList([]);
    setSearchTerm("");
    setError(null);
  };

  // Filter TCDS list based on search term
  React.useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredTcdsList(tcdsList);
    } else {
      const term = searchTerm.toLowerCase().trim();
      const filtered = tcdsList.filter((tcds) => {
        const tcdsNo = (tcds.tcds_no || "").toLowerCase();
        const sectionNumber = (tcds.section_number || "").toLowerCase();
        const customerName = (tcds.customer_name || tcds.customer || "").toLowerCase();
        const poNumber = (tcds.purchase_order_no || tcds.po_no || "").toLowerCase();
        const projectName = (tcds.project_name || "").toLowerCase();

        return (
          tcdsNo.includes(term) ||
          sectionNumber.includes(term) ||
          customerName.includes(term) ||
          poNumber.includes(term) ||
          projectName.includes(term)
        );
      });
      setFilteredTcdsList(filtered);
    }
  }, [searchTerm, tcdsList]);

  const handleCreateTCDS = () => {
    if (isAdmin) {
      // Admin must select a unit first
      setSelectedUnit("");
      setShowUnitModal(true);
    } else {
      navigateToCreateForm();
    }
  };

  const navigateToCreateForm = (unitId) => {
    const route = getRouteForType(selectedTcdsType);
    if (normalizeTcdsType(selectedTcdsType) === "Automation/Standard") {
      navigate(route, { state: { tcdsType: selectedTcdsType, unitId } });
      return;
    }
    navigate(route, { state: { unitId } });
  };

  const handleUnitConfirm = () => {
    if (!selectedUnit) return;
    setShowUnitModal(false);
    navigateToCreateForm(Number(selectedUnit));
  };

  const handleEditTCDS = (tcds) => {
    const route = getRouteForType(tcds.tcds_type, tcds.id);
    navigate(route);
  };

  const getBadgeClass = (type) => {
    if (type === "API 6D") return "badge-api-6d";
    if (type === "API 600") return "badge-api-600";
    return "badge-automation";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const handleDelete = async (id, sectionNumber) => {
    if (!window.confirm(`Are you sure you want to delete TCDS ${sectionNumber}?`)) {
      return;
    }

    try {
      setLoading(true);
      const row = tcdsList.find((item) => item.id === id);
      const deleteEndpoint = getDeleteEndpointForType(row?.tcds_type || selectedTcdsType, id);
      await axios.delete(deleteEndpoint);
      await fetchTCDS(selectedTcdsType);
    } catch (error) {
      setError(error.response?.data?.message || "Failed to delete TCDS");
      setLoading(false);
    }
  };

  const handleCopyTCDS = async (tcds) => {
    try {
      setLoading(true);
      const recordType = tcds.tcds_type || selectedTcdsType;
      const normalizedType = normalizeTcdsType(recordType);

      // Determine the endpoint based on TCDS type
      let copyEndpoint = `/tcds/copy/${tcds.id}`;
      if (normalizedType === "API 6D") {
        copyEndpoint = `/tcds/api-6d/copy/${tcds.id}`;
      } else if (normalizedType === "API 600") {
        copyEndpoint = `/tcds/api-600/copy/${tcds.id}`;
      }

      // Call the copy endpoint
      const response = await axios.post(copyEndpoint);

      if (response.data.success) {
        setError(null);
        // Refresh the TCDS list to show the newly copied TCDS
        await fetchTCDS(selectedTcdsType);
      } else {
        setError(response.data.message || "Failed to copy TCDS");
      }
    } catch (error) {
      setError(error.response?.data?.message || "Failed to copy TCDS");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (tcds) => {
    setSelectedTCDS(tcds);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTCDS(null);
  };

  const handleDownloadPdf = async (tcds) => {
    try {
      setLoading(true);
      // Fetch full TCDS data for PDF generation
      const recordType = tcds.tcds_type || selectedTcdsType;
      const response = await axios.get(getByIdEndpointForType(recordType, tcds.id));
      
      console.log("API Response:", response.data);
      
      const tcdsData = response.data.data || response.data;
      
      if (tcdsData) {
        try {
          // Use the appropriate PDF generator based on TCDS type
          if (normalizeTcdsType(recordType) === "API 6D") {
            generateAPI6DPdf(tcdsData);
          } else if (normalizeTcdsType(recordType) === "API 600") {
            // Call backend endpoint for API 600 PDF generation
            const pdfResponse = await axios.post('/pdf/download-api600', tcdsData, {
              responseType: 'blob'
            });
            
            // Create download link
            const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `TCDS_API600_${tcdsData.tcds_no || tcdsData.tcdsNo || 'export'}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          } else {
            generateTCDSPdf(tcdsData);
          }
        } catch (pdfError) {
          console.error("PDF Error:", pdfError);
          setError("Failed to generate PDF: " + pdfError.message);
        }
      } else {
        setError("Failed to fetch TCDS details for PDF generation");
      }
    } catch (error) {
      console.error("API Error:", error);
      setError(error.response?.data?.message || "Failed to generate PDF");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async (tcds) => {
    try {
      setLoading(true);
      const recordType = tcds.tcds_type || selectedTcdsType;
      const normalizedType = normalizeTcdsType(recordType);

      if (normalizedType === "Automation/Standard") {
        const tcdsNo = tcds.tcds_no;
        let sections = [];

        // ── Primary: use the /export endpoint which merges header + sections ──
        if (tcdsNo) {
          try {
            const exportResp = await axios.get(
              `/tcds/export/${encodeURIComponent(tcdsNo)}`
            );
            const exportData = exportResp.data.data || {};
            const tcdsDetails = exportData.tcdsDetails || {};
            const rawSections = exportData.sections || [];

            // Merge shared header fields into each section so Excel has full data
            sections = rawSections.map((sec) => ({
              ...tcdsDetails,   // shared header (customer, PO, dates, etc.)
              ...sec,           // section-specific data — overwrites header defaults
            }));
          } catch (exportErr) {
            console.warn("Export endpoint failed, falling back to sections endpoint:", exportErr.message);
          }
        }

        // ── Fallback 1: /tcds/sections/:tcdsNo ──────────────────────────────
        if (sections.length === 0 && tcdsNo) {
          try {
            const sectionsResp = await axios.get(
              `/tcds/sections/${encodeURIComponent(tcdsNo)}`
            );
            sections = sectionsResp.data.data || [];
          } catch (_) { /* ignore */ }
        }

        // ── Fallback 2: single record by ID ─────────────────────────────────
        if (sections.length === 0) {
          const resp = await axios.get(getByIdEndpointForType(recordType, tcds.id));
          const single = resp.data.data || resp.data;
          if (single) sections = [single];
        }

        if (sections.length === 0) {
          setError("Failed to fetch TCDS details for Excel generation");
          return;
        }

        try {
          // Debug: inspect merged section data to verify accessories_data field and structure
          console.log("[TCDS Excel] Sections to generate:", sections.length);
          sections.forEach((sec, i) => {
            const accs = typeof sec.accessories_data === "string"
              ? (() => { try { return JSON.parse(sec.accessories_data); } catch { return []; } })()
              : (sec.accessories_data || []);
            console.log(`[TCDS Excel] Section[${i}] accessories (${accs.length}):`,
              accs.map(a => ({
                name: a.accessoryName || a.name,
                model: a.model,
                paramCount: Array.isArray(a.parameters) ? a.parameters.length : "flat-obj",
                params: Array.isArray(a.parameters)
                  ? a.parameters.map(p => ({ name: p.name, value: p.value }))
                  : a.parameters,
              }))
            );
          });
          await generateTCDSExcel(sections);
        } catch (excelError) {
          console.error("Excel Error:", excelError);
          setError("Failed to generate Excel: " + excelError.message);
        }

      } else if (normalizedType === "API 6D") {
        const response = await axios.get(getByIdEndpointForType(recordType, tcds.id));
        const tcdsData = response.data.data || response.data;

        if (tcdsData) {
          try {
            const flattenedSectionData = {};
            if (tcdsData.section_data && typeof tcdsData.section_data === "object") {
              Object.values(tcdsData.section_data).forEach((section) => {
                if (section && typeof section === "object") {
                  Object.entries(section).forEach(([key, value]) => {
                    flattenedSectionData[key] = value;
                  });
                }
              });
            }

            const excelData = {
              ...tcdsData,
              ...flattenedSectionData,
            };

            delete excelData.section_data;

            const excelResponse = await axios.post("/excel6d/download-api6d", excelData, {
              responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([excelResponse.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              `TCDS_API6D_${tcdsData.tcds_no || tcdsData.tcdsNo || "export"}.xlsx`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
          } catch (excelError) {
            console.error("Excel Error:", excelError);
            setError("Failed to generate Excel: " + excelError.message);
          }
        } else {
          setError("Failed to fetch TCDS details for Excel generation");
        }
      } else if (normalizedType === "API 600") {
        const response = await axios.get(getByIdEndpointForType(recordType, tcds.id));
        const tcdsData = response.data.data || response.data;

        if (!tcdsData) {
          setError("Failed to fetch TCDS details for Excel generation");
          return;
        }

        const flattenedSectionData = {};
        if (tcdsData.section_data && typeof tcdsData.section_data === "object") {
          Object.values(tcdsData.section_data).forEach((section) => {
            if (section && typeof section === "object") {
              Object.entries(section).forEach(([key, value]) => {
                flattenedSectionData[key] = value;
              });
            }
          });
        }

        const excelData = {
          ...tcdsData,
          ...flattenedSectionData,
        };

        delete excelData.section_data;

        const excelResponse = await axios.post("/excel/download-api600", excelData, {
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([excelResponse.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `TCDS_API600_${tcdsData.tcds_no || tcdsData.tcdsNo || "export"}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        setError(null);
      } else {
        setError("Excel download is not available for this TCDS type");
      }
    } catch (error) {
      console.error("API Error:", error);
      setError(error.response?.data?.message || "Failed to generate Excel");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tcds-container">
      <button className="tcds-back-button" onClick={() => navigate("/dashboard")}>
        ← Back to Dashboard
      </button>

      <div className="tcds-header">
        <h1 className="tcds-title">TCDS Type Selection Screen</h1>
        <p className="tcds-subtitle">
          Select the type of TCDS to view records
        </p>
      </div>

      <div className="tcds-type-selection">
        <div className="tcds-type-cards">
          {/* Automation/Standard Card */}
          <div
            className="tcds-type-card automation"
            onClick={() => handleCardClick("Automation/Standard")}
          >
            <div className="tcds-card-content">
              <h3 className="tcds-card-title">TCDS Automation / Standard</h3>
              <span className="tcds-card-badge">Standard</span>
            </div>
          </div>

          {/* API 6D Card */}
          <div
            className="tcds-type-card api-6d"
            onClick={() => handleCardClick("API 6D")}
          >
            <div className="tcds-card-content">
              <h3 className="tcds-card-title">TCDS API 6D</h3>
              <span className="tcds-card-badge">API 6D</span>
              <p className="tcds-card-note">Check Valves Only</p>
            </div>
          </div>

          {/* API 600 Card */}
          <div
            className="tcds-type-card api-600"
            onClick={() => handleCardClick("API 600")}
          >
            <div className="tcds-card-content">
              <h3 className="tcds-card-title">TCDS API 600</h3>
              <span className="tcds-card-badge">API 600</span>
              <p className="tcds-card-note">Gate Valves Only</p>
            </div>
          </div>
        </div>
      </div>

      {/* Table Modal */}
      {showTableModal && (
        <div className="table-modal-overlay" onClick={closeTableModal}>
          <div className="table-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="table-modal-header">
              <div>
                <h2 className="table-modal-title">TCDS - {selectedTcdsType} Records</h2>
                <p className="table-modal-subtitle">View and manage technical compliance data sheets</p>
              </div>
              <div className="table-modal-actions">
                {canCreate && (
                  <button
                    className="btn-create-tcds"
                    onClick={handleCreateTCDS}
                  >
                    + Create TCDS
                  </button>
                )}
                <button className="modal-close-btn" onClick={closeTableModal}>
                  ×
                </button>
              </div>
            </div>

            <div className="table-modal-search">
              <input
                type="text"
                placeholder="Search by TCDS No., Section No., Customer, PO, Project..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button className="clear-search" onClick={() => setSearchTerm("")}>
                  ×
                </button>
              )}
              <span className="search-results-count">
                {filteredTcdsList.length} of {tcdsList.length} records
              </span>
            </div>

            <div className="table-modal-content">
              {loading ? (
                <div className="loading-message">Loading TCDS records...</div>
              ) : error ? (
                <div className="error-message">{error}</div>
              ) : filteredTcdsList.length === 0 ? (
                <div className="empty-state">
                  <p>No TCDS records found</p>
                  {canCreate && (
                    <button
                      className="btn-create-first"
                      onClick={handleCreateTCDS}
                    >
                      Create First TCDS
                    </button>
                  )}
                </div>
              ) : (
                <div className="tcds-table-container">
                  <table className="tcds-table">
                    <thead>
                      <tr>
                        <th>TCDS No.</th>
                        <th>Section No.</th>
                        <th>Type</th>
                        <th>Customer Name</th>
                        <th>PO Number</th>
                        <th>Project Name</th>
                        <th>Created Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTcdsList.map((tcds) => (
                        <tr key={tcds.id}>
                          <td>{tcds.tcds_no || "N/A"}</td>
                          <td>{tcds.section_number || "N/A"}</td>
                          <td>
                            <span className={`badge ${getBadgeClass(tcds.tcds_type)}`}>
                              {tcds.tcds_type}
                            </span>
                          </td>
                          <td>{tcds.customer_name || tcds.customer || "N/A"}</td>
                          <td>{tcds.purchase_order_no || tcds.po_no || "N/A"}</td>
                          <td>{tcds.project_name || "N/A"}</td>
                          <td>{formatDate(tcds.created_at)}</td>
                          <td>
                            <div className="action-buttons">
                              {(normalizeTcdsType(tcds.tcds_type) === "Automation/Standard" || normalizeTcdsType(tcds.tcds_type) === "API 6D" || normalizeTcdsType(tcds.tcds_type) === "API 600") && (
                                <button
                                  className="btn-action btn-download"
                                  onClick={() => handleDownloadExcel(tcds)}
                                  title="Download Excel"
                                  style={{ background: '#d1fae5', color: '#059669' }}
                                >
                                  Excel
                                </button>
                              )}
                              <button
                                className="btn-action btn-copy"
                                onClick={() => handleCopyTCDS(tcds)}
                                title="Copy"
                                style={{ background: '#fef3c7', color: '#b45309' }}
                              >
                                Copy
                              </button>
                              <button
                                className="btn-action btn-edit"
                                onClick={() => handleEditTCDS(tcds)}
                                title="Edit"
                              >
                                Edit
                              </button>
                              <button
                                className="btn-action btn-delete"
                                onClick={() => handleDelete(tcds.id, tcds.section_number || tcds.tcds_no)}
                                title="Delete"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Unit Selection Modal for Admin */}
      {showUnitModal && (
        <div className="modal-overlay unit-modal-overlay" style={{ zIndex: 3000 }} onClick={() => setShowUnitModal(false)}>
          <div className="modal-container unit-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: "18px", fontWeight: 600, color: "#1e3a8a" }}>Select Unit</h2>
              <button className="modal-close" onClick={() => setShowUnitModal(false)}>×</button>
            </div>
            <div className="modal-content unit-modal-content">
              <p style={{ marginBottom: "16px", color: "#555", fontSize: "14px" }}>
                Select the unit for which you are creating this TCDS ({selectedTcdsType}).
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                {units.map((unit) => (
                  <label
                    key={unit.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "12px 16px",
                      border: selectedUnit === String(unit.id) ? "2px solid #1d4ed8" : "1px solid #d1d5db",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor: selectedUnit === String(unit.id) ? "#eff6ff" : "#fff",
                    }}
                  >
                    <input
                      type="radio"
                      name="unit"
                      value={unit.id}
                      checked={selectedUnit === String(unit.id)}
                      onChange={(e) => setSelectedUnit(e.target.value)}
                    />
                    <span style={{ fontWeight: "500", fontSize: "14px" }}>{unit.name}</span>
                  </label>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px", flexShrink: 0, minHeight: "50px" }}>
                <button
                  style={{
                    padding: "10px 20px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: 600,
                    background: "#fff",
                    color: "#374151",
                  }}
                  onClick={() => setShowUnitModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-create-tcds"
                  disabled={!selectedUnit}
                  style={{ opacity: selectedUnit ? 1 : 0.5, cursor: selectedUnit ? "pointer" : "not-allowed" }}
                  onClick={handleUnitConfirm}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showModal && selectedTCDS && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>TCDS Details - {selectedTCDS.section_number}</h2>
              <button className="modal-close" onClick={closeModal}>
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="modal-section">
                <h3>Section Information</h3>
                <div className="modal-grid">
                  <div className="modal-field">
                    <label>Section Number:</label>
                    <span>{selectedTCDS.section_number || "N/A"}</span>
                  </div>
                  <div className="modal-field">
                    <label>Section Title:</label>
                    <span>{selectedTCDS.section_title || "N/A"}</span>
                  </div>
                  <div className="modal-field">
                    <label>TCDS Type:</label>
                    <span className={`badge ${getBadgeClass(selectedTCDS.tcds_type)}`}>
                      {selectedTCDS.tcds_type}
                    </span>
                  </div>
                </div>
              </div>

              <div className="modal-section">
                <h3>Customer Information</h3>
                <div className="modal-grid">
                  <div className="modal-field">
                    <label>Customer Name:</label>
                    <span>{selectedTCDS.customer_name || "N/A"}</span>
                  </div>
                  <div className="modal-field">
                    <label>Purchase Order No:</label>
                    <span>{selectedTCDS.purchase_order_no || "N/A"}</span>
                  </div>
                  <div className="modal-field">
                    <label>PO Date:</label>
                    <span>{formatDate(selectedTCDS.po_date)}</span>
                  </div>
                  <div className="modal-field">
                    <label>Project Name:</label>
                    <span>{selectedTCDS.project_name || "N/A"}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageTCDS;
